/**
 * The sound of a screen share: its own switch, not the microphone's.
 *
 * From 2026-10-12 it followed the microphone, which ended "everyone is muted
 * but there is still sound" — and made a presenter who wanted the room to hear
 * a video open their mic to the room as well. Now (2026-10-29):
 *
 *  - muting yourself leaves the share's sound alone;
 *  - the presenter switches it on and off on the stage (`shareAudioOn` in the
 *    session store);
 *  - a moderator's mute — one person or "mute all" — still turns it off, and
 *    turning it back on is held to the microphone's room rule (`canShareAudio`);
 *  - viewers see a speaker on the share while its sound is live, from the SFU's
 *    `producerPaused` for the share's audio producer (`peersWithLiveShareAudio`),
 *    because a muted tile with sound coming from somewhere is the confusion
 *    the old rule was fixing.
 */
import { canPressDevice } from './device-gate'
import { isScreenSourceType } from './share-stopped'

/**
 * May this person switch their share's sound ON?
 *
 * The microphone's rule, because both are this person being heard by the room:
 * a host who switched the microphone off for attendees has not let them play
 * sound into it either. A personal grant (an accepted Request Audio) and the
 * moderator exemption carry over. Switching it OFF is never blocked.
 */
export function canShareAudio(input: {
  viewOnly: boolean
  isModerator: boolean
  roomAllows: boolean
  granted: boolean
}): boolean {
  return canPressDevice({ ...input, isOn: false })
}

/**
 * The peers whose share sound this viewer is receiving live: a share audio
 * consumer exists, and its producer is not paused.
 *
 * `paused` is keyed by peer, like the microphone's: one share per peer.
 */
export function peersWithLiveShareAudio(
  consumers: Iterable<{ peerId: string; kind: string; sourceType: string }>,
  paused: ReadonlySet<string>,
  self: string | null,
): Set<string> {
  const live = new Set<string>()
  for (const c of consumers) {
    if (c.peerId === self || c.kind !== 'audio' || !isScreenSourceType(c.sourceType)) continue
    if (!paused.has(c.peerId)) live.add(c.peerId)
  }
  return live
}

export function sameMembers(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false
  for (const x of a) if (!b.has(x)) return false
  return true
}
